import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';

import LoaderComponent from '@components/LoaderComponent';
import NoData from '@components/NoData';
import OrderLists from '@components/OrderLists';
import { toastError } from '@components/Toast';

import BarChart from '@features/dashboard/components/admin-dashboard/BarChart';

import {
	onSetOrderSearch,
	onSetSelectedBrand,
	onSetSelectedManager
} from '@store/dashboard/commercial-dashboard/actions';
import { getOrderSearchValue, getSelectedBrand, getSelectedManager } from '@store/reducers';

import ApiClient from '@lib/apiClient';

import { isPageReachBottom } from '@utils/Util';

const CommercialDashboard = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [isShipmentLoading, setIsShipmentLoading] = useState(false);
	const [isPiSalesContractLoading, setIsPiSalesContractLoading] = useState(false);
	const [brandLists, setBrandLists] = useState([]);
	const [upcomingShipments, setUpcomingShipments] = useState({});
	const [piSalesContracts, setPiSalesContracts] = useState({});
	const filteredBrand = useSelector(getSelectedBrand);
	const [selectedBrand, setSelectedBrand] = useState(filteredBrand);
	const filteredManager = useSelector(getSelectedManager);
	const [selectedManager, setSelectedManager] = useState(filteredManager);
	const [accountManager, setAccountManager] = useState([]);
	const [managerLists, setManagerLists] = useState([]);
	const [accountManagerList, setAccountManagerList] = useState([]);
	const filteredOrderSearchValue = useSelector(getOrderSearchValue);
	const [searchValue, setSearchValue] = useState(filteredOrderSearchValue);
	const [activeStep, setActiveStep] = useState('SCPI');
	const dispatch = useDispatch();

	// useEffect(() => {
	// 	document.addEventListener('mousedown', handleClickOutside);
	// 	return () => {
	// 		document.removeEventListener('mousedown', handleClickOutside);
	// 	};
	// }, []);

	// const handleClickOutside = (e) => {
	// 	if (myRef && !myRef.current.contains(e.target)) {
	// 		if (dateRangeRef && dateRangeRef.current === false) {
	// 			setIsOpenDropdown(false);
	// 		}
	// 	}
	// };

	// useEffect(() => {
	// 	getCommercialDashboard();
	// }, [sortType]);

	useEffect(() => {
		getBrandLists();
		fetchManagers();
	}, []);

	const fetchManagers = async () => {
		let params = {
			userType: 'PROJECT_MANAGER',
			page: 0,
			size: 1000
		};
		await ApiClient.GET('getProjectUserSuggestions', params)
			.then(({ data }) => {
				if (data) {
					const result = data?.map((item) => {
						return {
							value: item.id,
							label: item.name,
							emai: item.email
						};
					});
					setManagerLists(result);
				}
			})
			.catch(({ response }) => {
				this.setState({ loading: false });
				if (response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
		params = {
			userType: 'ACCOUNT_MANAGER',
			page: 0,
			size: 1000
		};
		await ApiClient.GET('getProjectUserSuggestions', params)
			.then(({ data }) => {
				if (data) {
					const result = data?.map((item) => {
						return {
							value: item.id,
							label: item.name,
							emai: item.email
						};
					});
					setAccountManagerList(result);
				}
			})
			.catch(({ response }) => {
				this.setState({ loading: false });
				if (response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, data } = scrollRef.current;
			if (totalElements > 0) {
				if (totalElements !== data.length) {
					setPage((page) => page + 1);
				}
			}
		}
	};

	const scrollRef = useRef({});
	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	useEffect(() => {
		if (activeStep === 'SCPI') setCurrentState(piSalesContracts);
		else setCurrentState(upcomingShipments);
	}, [upcomingShipments, piSalesContracts, activeStep]);

	useEffect(() => {
		if (page === 0 && !piSalesContracts.data && !upcomingShipments.data) {
			getPiAndSalesContractList();
			getUpcomingShipments();
		} else if (activeStep === 'SCPI') getPiAndSalesContractList();
		else getUpcomingShipments();
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, [page, selectedBrand, selectedManager, accountManager, searchValue]);

	const getUpcomingShipments = async () => {
		setIsShipmentLoading(true);
		let params = `?page=${page}&size=20`;
		if (selectedBrand?.value) {
			params += `&brandIdList=${selectedBrand.value}`;
		}
		if (selectedManager?.value) {
			params += `&teamIdList=${selectedManager.value}`;
		}
		if (accountManager?.value) {
			params += `&teamIdList=${accountManager.value}`;
		}
		if (searchValue) {
			params += `&search=${searchValue}`;
		}
		await ApiClient.GET('getUpcomingShipments', params)
			.then((response) => {
				setIsShipmentLoading(false);
				if (page === 0) {
					setUpcomingShipments(response.data);
				} else {
					let prevData = upcomingShipments?.data || [];
					let newData = { ...response?.data, data: [...prevData, ...(response?.data.data || [])] };
					setUpcomingShipments(newData);
				}
			})
			.catch(({ response }) => {
				setIsShipmentLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const getPiAndSalesContractList = async () => {
		setIsPiSalesContractLoading(true);
		let params = `?page=${page}&size=20`;
		if (selectedBrand?.value) {
			params += `&brandId=${selectedBrand.value}`;
		}
		if (selectedManager?.value) {
			params += `&projectManager=${selectedManager.value}`;
		}
		if (accountManager?.value) {
			params += `&accountManager=${accountManager.value}`;
		}
		if (searchValue) {
			params += `&search=${searchValue}`;
		}
		await ApiClient.GET('getPiAndSalesContractList', params)
			.then((response) => {
				setIsPiSalesContractLoading(false);
				if (page === 0) {
					setPiSalesContracts(response.data);
				} else {
					let prevData = piSalesContracts?.data || [];
					let newData = { ...response?.data, data: [...prevData, ...(response?.data.data || [])] };
					setPiSalesContracts(newData);
				}
			})
			.catch(({ response }) => {
				setIsPiSalesContractLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const getBrandLists = async () => {
		await ApiClient.GET('getBrandBasicList', '?page=0&size=1000')
			.then(({ data }) => {
				if (data) {
					const result = data?.data?.map((item) => {
						return {
							value: item.id,
							label: item.name
						};
					});
					setBrandLists(result);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const onSelectBrand = (selected) => {
		if (selected === null) {
			setSelectedBrand(selected);
			dispatch(onSetSelectedBrand({ value: '', label: '' }));
		} else {
			setPage(0);
			setSelectedBrand(selected);
			dispatch(onSetSelectedBrand(selected));
		}
	};

	const onSelectManager = (selected) => {
		if (selected === null) {
			setSelectedManager(selected);
			dispatch(onSetSelectedManager({ value: '', label: '' }));
		} else {
			setPage(0);
			setSelectedManager(selected);
			dispatch(onSetSelectedManager(selected));
		}
	};

	const onSearchChange = (e) => {
		setPage(0);
		setSearchValue(e.target.value);
		dispatch(onSetOrderSearch(e.target.value));
	};

	const onTabChange = (tabType) => {
		setPage(0);
		setActiveStep(tabType);
	};

	const styles = {
		control: (base) => ({
			...base,
			fontFamily: 'Inter',
			fontSize: '14px'
		}),
		menu: (base) => ({
			...base,
			fontFamily: 'Inter',
			fontSize: '14px'
		})
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="full-dashboard">
				<BarChart />
			</div>
			<div className="commercial-invoice-content">
				{/* <div className="commercial-dashboard-container">
					<div className="left-half full-summmary">
						<div className="w-100">
							<h2 className="regular-14 gray_dark_01">
								<span className="gray_dark_02">SHIPMENT IN </span>
								{currentMonthShipment &&
									getMothNameFromNumber(currentMonthShipment?.month).toUpperCase()}
								, &nbsp;
								{currentMonthShipment?.year}
							</h2>
						</div>
						<div className="summary-details">
							<div className="shipment-summary">
								<div className="status-box">
									{currentMonthShipment?.order && (
										<h3>{getNumberUnit(currentMonthShipment?.order)}$</h3>
									)}
									<div className="status-info shipment">
										<span className="status-icon" />
										<span className="regular-12">Target shipment</span>
									</div>
								</div>
								<div className="status-box mt-3">
									{currentMonthShipment?.shipment && (
										<h3>{getNumberUnit(currentMonthShipment?.shipment)}$</h3>
									)}
									<div className="status-info order">
										<span className="status-icon" />
										<span className="regular-12">Actual shipment</span>
									</div>
								</div>
							</div>
						</div>
						<Tooltip
							title={
								<div>
									<p className="regular-14">Target: ${currentMonthShipment?.order}</p>
									<p className="regular-14">Shipped: ${currentMonthShipment?.shipment}</p>
								</div>
							}
							placement="top"
							arrow
						>
							<div className="shipment-and-order-progress">
								<div className="shipment-progress">
									<div
										className="progress-count"
										style={{
											height:
												getParcentage(
													currentMonthShipment?.order,
													currentMonthShipment?.shipment
												) + '%'
										}}
									/>
								</div>
								<div className="order-progress">
									<div
										className="progress-count"
										style={{
											height:
												getParcentage(
													currentMonthShipment?.shipment,
													currentMonthShipment?.order
												) + '%'
										}}
									/>
								</div>
							</div>
						</Tooltip>
					</div>
					<div className="right-half date-wise-summery">
						<div className="summary-details">
							<div className="select-component" ref={myRef}>
								<div
									className="click-box click mb-1 border-0"
									onClick={() => setIsOpenDropdown(!isOpenDropdown)}
								>
									<p className="regular-14">
										{sortType?.type != 'Custom'
											? sortType?.type
											: changeDateFormat(sortType?.value?.startDate, '', 'MMM,YY') +
											  ' - ' +
											  changeDateFormat(sortType?.value?.endDate, '', 'MMM,YY')}
									</p>
									<img src="%PUBLIC_URL%/images/chevron_down.svg" alt />
								</div>
								{isOpenDropdown && (
									<div className="dropdown-box">
										<div className="list-items scroll-y-label">
											<div
												className="single-item"
												onClick={() => onSortCLick('ONE_YEAR')}
											>
												<p>Last 1 year</p>
											</div>
											<div
												className="single-item"
												onClick={() => onSortCLick('SIX_MONTH')}
											>
												<p>Last 6 month</p>
											</div>
											<div
												className="single-item"
												onClick={() => onSortCLick('FOUR_MONTH')}
											>
												<p>Last 4 month</p>
											</div>
											<div
												className="single-item"
												onClick={() => onSortCLick('LAST_MONTH')}
											>
												<p>Last month</p>
											</div>
											<div className="single-item">
												<DateRangePickerComponent
													open={() => (dateRangeRef.current = true)}
													close={() => (dateRangeRef.current = false)}
													blur={() => setIsOpenDropdown(false)}
													format="MMM/yyyy"
													start={'Year'}
													depth={'Year'}
													placeholder="Choose a date range"
													change={onChangeMonthRange}
													cleared={() => onSortCLick('ONE_YEAR', true)}
													value={{
														start: sortType?.value?.startDate,
														end: sortType?.value?.endDate
													}}
													openOnFocus={true}
													strictMode={true}
													// showClearButton={true}
												/>
											</div>
										</div>
									</div>
								)}
							</div>

							<div className="shipment-summary">
								<div className="status-box">
									{searchableShipmentResponse?.totalOrder && (
										<h3>{getNumberUnit(searchableShipmentResponse?.totalOrder)}$</h3>
									)}
									<div className="status-info shipment">
										<span className="regular-12">Target shipment</span>
									</div>
								</div>
								<div className="status-box mt-3">
									{searchableShipmentResponse?.totalShipment && (
										<h3>{getNumberUnit(searchableShipmentResponse?.totalShipment)}$</h3>
									)}
									<div className="status-info order">
										<span className="regular-12">Actual shipment</span>
									</div>
								</div>
							</div>
						</div>
						<div className="all-progress-summary scroll-x-label">
							{searchableShipmentResponse?.monthWiseShipment?.map((item, idx) => (
								<div className="single-bar" key={`${generateRandomString()}-${idx}`}>
									<Tooltip
										title={
											<div>
												<p className="regular-14">Target: ${item?.order}</p>
												<p className="regular-14">Shipped: ${item?.shipment}</p>
											</div>
										}
										placement="top"
										arrow
									>
										<div className="shipment-and-order-progress">
											<div className="shipment-progress">
												<div
													className="progress-count"
													style={{
														height:
															(item?.order * 100) /
																searchableShipmentResponse?.maximum +
															'%'
													}}
												/>
											</div>

											<div className="order-progress">
												<div
													className="progress-count"
													style={{
														height:
															(item?.shipment * 100) /
																searchableShipmentResponse?.maximum +
															'%'
													}}
												/>
											</div>
										</div>
									</Tooltip>
									<span className="status-date">
										{getMothNameFromNumber(item?.month).toUpperCase()},&nbsp;
										{item?.year}
									</span>
								</div>
							))}
						</div>
					</div>
				</div> */}
				<div className="coommercial-order-list-container">
					<div className="orders-filter-top d-flex justify-content-between align-items-center mb-2">
						<div className="title-with-order-count">
							<div className="cm-order-chips">
								<span
									className={`${activeStep === 'SCPI' ? 'chip active' : 'chip'}`}
									onClick={() => {
										onTabChange('SCPI');
									}}
								>
									PI & SC(
									{isNaN(piSalesContracts?.totalElements)
										? 0
										: piSalesContracts?.totalElements}
									)
								</span>
								<span
									className={`${activeStep === 'CM' ? 'chip active' : 'chip'}`}
									onClick={() => {
										onTabChange('CM');
									}}
								>
									Commercial invoice(
									{isNaN(upcomingShipments?.totalElements)
										? 0
										: upcomingShipments?.totalElements}
									)
								</span>
							</div>
						</div>
						<div className="filter-actions collections-filter-actions">
							<div className="form-group size32 m-0 search-filter filter-with-animation">
								<div className={'with-icon right-icon'}>
									<input
										type="text"
										autoComplete="off"
										placeholder="PO, style no & orde title"
										onChange={(e) => onSearchChange(e)}
										value={searchValue}
									/>
									<span className="right-icon">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width={23}
											height={22}
											viewBox="0 0 23 22"
											fill="none"
											className="cursor-pointer"
										>
											<path
												d="M10.543 16.5C14.3399 16.5 17.418 13.422 17.418 9.625C17.418 5.82804 14.3399 2.75 10.543 2.75C6.74601 2.75 3.66797 5.82804 3.66797 9.625C3.66797 13.422 6.74601 16.5 10.543 16.5Z"
												stroke="#21242B"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
											<path
												d="M19.2526 18.3346L15.5859 14.668"
												stroke="#21242B"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</span>
								</div>
							</div>
							<div className="selection-filters d-flex flex-warp">
								<Select
									styles={styles}
									options={brandLists}
									isClearable
									onChange={onSelectBrand}
									placeholder={'Brands'}
									isSearchable={true}
									value={selectedBrand}
								/>

								<Select
									styles={styles}
									options={accountManagerList}
									isClearable
									onChange={setAccountManager}
									placeholder="Account manager"
									isSearchable={true}
								/>

								<Select
									styles={styles}
									options={managerLists}
									isClearable
									onChange={onSelectManager}
									placeholder="Project manager"
									isSearchable={true}
									value={selectedManager}
								/>
							</div>
						</div>
					</div>

					{activeStep === 'SCPI' ? (
						<>
							{piSalesContracts?.data?.length === 0 ? (
								<NoData message={'No data found'} />
							) : (
								<LoaderComponent loading={isPiSalesContractLoading}>
									<OrderLists
										orderStore={piSalesContracts?.data}
										source="SCPI"
										// isRedirectToCommercialInvoice={true}
									/>
								</LoaderComponent>
							)}
						</>
					) : (
						<>
							{upcomingShipments?.data?.length === 0 ? (
								<NoData message={'No shipment found'} />
							) : (
								<LoaderComponent loading={isShipmentLoading}>
									<OrderLists orderStore={upcomingShipments?.data} source="CM" />
								</LoaderComponent>
							)}
						</>
					)}
				</div>
			</div>
		</LoaderComponent>
	);
};

export default CommercialDashboard;
